import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ApplicationFormSection() {
  const [form, setForm] = useState({
    company_name: "",
    contact_email: "",
    tender_description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_email || !form.tender_description) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("company_name", form.company_name);
      formData.append("contact_email", form.contact_email);
      formData.append("tender_description", form.tender_description);

      await axios.post(`${API}/tender-submissions`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSubmitted(true);
      toast.success("Demo request submitted successfully!");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm({ company_name: "", contact_email: "", tender_description: "" });
    setSubmitted(false);
  };

  return (
    <section id="application-form" data-testid="application-form-section" className="py-10 md:py-[85px] bg-[#F8FAFC]">
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left: Copy */}
          <div className="lg:sticky lg:top-32">
            <span className="inline-block text-xs font-medium uppercase tracking-widest text-[#3B82F6] mb-4">
              Get Started
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0F172A] tracking-tight text-balance mb-4">
              Book a{" "}
              <span className="text-[#3B82F6]">Free Demo</span>
            </h2>
            <p className="text-base text-slate-600 leading-relaxed mb-8">
              Tell us about your business and we'll show you exactly how<br className="hidden lg:inline" /> AICallFront can handle your calls, capture leads, and book<br className="hidden lg:inline" /> appointments automatically.
            </p>

            <div className="space-y-4">
              {[
                "See the AI receptionist handle a live call",
                "Get a custom setup for your business type",
                "No commitment — just a quick walkthrough",
                "Up and running in under 24 hours",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#3B82F6]/10 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#3B82F6]" strokeWidth={2} />
                  </div>
                  <span className="text-sm text-slate-600">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div>
            {submitted ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10 text-center animate-scale-in" data-testid="form-success-message">
                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-2">
                  Demo Request Submitted
                </h3>
                <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
                  We've received your request. Our team will be in touch within 24 hours to schedule your personalised demo.
                </p>
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="rounded-lg"
                  data-testid="submit-another-button"
                >
                  Submit Another Request
                </Button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm"
                data-testid="tender-submission-form"
              >
                <div className="space-y-5">
                  <div>
                    <Label htmlFor="company_name" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                      Business Name *
                    </Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      value={form.company_name}
                      onChange={handleChange}
                      placeholder="Your business name"
                      className="h-12 bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-lg"
                      data-testid="input-company-name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_email" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                      Contact Email *
                    </Label>
                    <Input
                      id="contact_email"
                      name="contact_email"
                      type="email"
                      value={form.contact_email}
                      onChange={handleChange}
                      placeholder="you@business.com"
                      className="h-12 bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-lg"
                      data-testid="input-contact-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="tender_description" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                      Tell Us About Your Business *
                    </Label>
                    <Textarea
                      id="tender_description"
                      name="tender_description"
                      value={form.tender_description}
                      onChange={handleChange}
                      placeholder="What type of business do you run? How many calls do you get per day? What's your biggest challenge with phone enquiries?"
                      className="min-h-[120px] bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-lg resize-none"
                      data-testid="input-tender-description"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#0F172A] text-white h-12 rounded-lg font-medium hover:bg-[#1E293B] transition-all shadow-sm hover:shadow-md disabled:opacity-70"
                    data-testid="submit-tender-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Book My Free Demo"
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
